import React from "react";
import { FaCalendar, FaUserTie } from "react-icons/fa";
import CustomInput from "../../common/CustomInput";
import StaffProfileDropdown from "../../common/StaffProfileDropdown";
import StaffProfileDropdownWithFreeText from "../../common/StaffProfileDropdownWithFreeText";
import PhoneNumberField from "../common/PhoneNumberField";

const ClubDetailsSection = ({
  isEditing,
  isSaving,
  onEdit,
  onCancel,
  onSaveClick,
  formData,
  handleInputChange,
  positionOptions,
  nationalityOptions,
  onOpenImageUpload,
  onOpenChangePassword,
  onDocumentUpload,
  isLoadingDocuments,
  onOpenMobileModal,
  dateOfBirthError,
}) => {
  return (
    <div className="bg-white p-6 border border-[#E6E6E6]">
      <div className="flex justify-between items-center gap-3 mb-6 flex-col sm:flex-row">
        <h2 className="text-[11px] font-bold text-[#3C3C3C] self-start">STAFF DETAILS</h2>
      <div className="self-end">
          {!isEditing ? (
          <div className="flex items-center gap-2">
            <button
              onClick={onOpenChangePassword}
              className="border border-[#E6E6E6] px-4 py-2 rounded-lg text-[#1E1E1E]"
            >
              Change password
            </button>
            <button
              onClick={onEdit}
              className="bg-[#2283FF] px-4 py-2 text-[#F5F5F5] rounded-lg"
            >
              Edit profile
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={onCancel}
              disabled={isSaving}
              className="border border-[#E6E6E6] px-4 py-2 rounded-lg text-[#1E1E1E]"
            >
              Cancel
            </button>
            <button
              onClick={onSaveClick}
              disabled={isSaving}
              className="bg-[#2283FF] px-4 py-2 text-[#F5F5F5] rounded-lg"
            >
              {isSaving ? "Saving..." : "Save changes"}
            </button>
          </div>
        )}
      </div>
      </div>

      {/* Profile Picture Row */}
      <div className="flex items-center gap-6 mb-6">
        <div className="flex-shrink-0 relative">
          <div
            className="w-24 h-24 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden cursor-pointer"
            onClick={() => isEditing && onOpenImageUpload()}
          >
            {formData.profileImage ? (
              <img src={formData.profileImage} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <FaUserTie className="text-3xl text-gray-600" />
            )}
          </div>
          <div className="absolute bottom-0 right-0 w-6 h-6 bg-[#2283FF] rounded-full flex items-center justify-center">
            <span className="text-white text-xs font-bold">TP</span>
          </div>
        </div>
      </div>

      {/* Row 1: First Name and Last Name */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <CustomInput
          label="First name"
          value={formData.firstName}
          onChange={(e) => handleInputChange("firstName", e.target.value)}
          placeholder="Enter first name"
          required
          disabled={!isEditing}
        />
        <CustomInput
          label="Last name"
          value={formData.lastName}
          onChange={(e) => handleInputChange("lastName", e.target.value)}
          placeholder="Enter last name"
          required
          disabled={!isEditing}
        />
      </div>

      {/* Row 2: Position and Nationality */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <StaffProfileDropdown
          label="Position"
          value={formData.position}
          onChange={(value) => handleInputChange("position", value)}
          options={positionOptions}
          placeholder="Select position"
          disabled={true} // Position is read-only
        />
        <StaffProfileDropdownWithFreeText
          label="Nationality"
          value={formData.nationality}
          onChange={(value) => handleInputChange("nationality", value)}
          options={nationalityOptions}
          placeholder="Select nationality or type..."
          disabled={!isEditing}
        />
      </div>

      {/* Row 3: Birthday, Height, Weight */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        {/* Birthday is OPTIONAL — the same treatment the coach profile gives
            its date of birth. Left empty the profile is still valid and
            complete; no age is shown anywhere, rather than "0 yrs". A
            rejected date reports itself on the field (dateOfBirthError). */}
        <div className="relative">
          <CustomInput
            label="Birthday"
            type="date"
            value={formData.birthday}
            onChange={(e) => handleInputChange("birthday", e.target.value)}
            placeholder="Optional"
            icon={<FaCalendar className="text-gray-400" />}
            disabled={!isEditing}
            error={dateOfBirthError}
            max={new Date().toISOString().split("T")[0]}
          />
        </div>
        <CustomInput
          label="Height"
          value={formData.height}
          onChange={(e) => handleInputChange("height", e.target.value)}
          placeholder="Enter height"
          required
          disabled={!isEditing}
        />
        <CustomInput
          label="Weight"
          value={formData.weight}
          onChange={(e) => handleInputChange("weight", e.target.value)}
          placeholder="Enter weight"
          required
          disabled={!isEditing}
        />
      </div>

      {/* Row 4: Email Address and Phone Number */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <CustomInput
          label="Email address"
          value={formData.email}
          onChange={(e) => handleInputChange("email", e.target.value)}
          placeholder="Enter email address"
          type="email"
          required
          disabled={true}
        />
        <PhoneNumberField
          label="Phone number"
          value={formData.mobile}
          disabled
          showEdit={isEditing}
          onEdit={onOpenMobileModal}
        />
      </div>

      {/* Row 5: Website, LinkedIn, Social */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <CustomInput
          label="Website (Optional)"
          value={formData.website}
          onChange={(e) => handleInputChange("website", e.target.value)}
          placeholder="https://www.xyz.com"
          disabled={!isEditing}
        />
        <CustomInput
          label="Linkedin (Optional)"
          value={formData.linkedin}
          onChange={(e) => handleInputChange("linkedin", e.target.value)}
          placeholder="https://www.xyz.com"
          disabled={!isEditing}
        />
        <CustomInput
          label="Social (Optional)"
          value={formData.social}
          onChange={(e) => handleInputChange("social", e.target.value)}
          placeholder="https://www.xyz.com"
          disabled={!isEditing}
        />
      </div>

      {/* CV / Resume Upload */}
      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">CV / Resume</label>
        {formData.cvResume ? (
          <div className="flex items-center gap-3 p-3 bg-[#F8F9FB] border border-gray-300 rounded-md">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 2H4C3.44772 2 3 2.44772 3 3V17C3 17.5523 3.44772 18 4 18H16C16.5523 18 17 17.5523 17 17V7L11 2H8Z" stroke="#006BF3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M11 2V7H17" stroke="#006BF3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="text-sm text-gray-700">{formData.cvResume.name}</span>
            {isEditing && (
              <button
                onClick={() => handleInputChange("cvResume", null)}
                className="ml-auto text-red-500 hover:text-red-700"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center w-full h-20 border-2 border-dashed border-gray-300 rounded-md bg-[#F8F9FB]">
            <input
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={async (e) => {
                const file = e.target.files && e.target.files[0];
                if (file && onDocumentUpload) {
                  try {
                    await onDocumentUpload('resume', file.name, file);
                    handleInputChange("cvResume", file);
                  } catch (error) {
                    console.error('CV/Resume upload failed:', error);
                  }
                }
              }}
              className="hidden"
              id="cv-resume-upload"
              disabled={!isEditing || isLoadingDocuments}
            />
            <label
              htmlFor="cv-resume-upload"
              className={`cursor-pointer text-sm text-gray-500 ${(!isEditing || isLoadingDocuments) ? 'cursor-not-allowed opacity-50' : ''}`}
            >
              {isLoadingDocuments ? "Uploading..." : isEditing ? "Click to upload CV/Resume" : "No file uploaded"}
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClubDetailsSection;

